import { createContext, useContext } from 'react';
import type { GiftReceivedEvent } from '@/lib/api/gifts';

type JsonPrimitive = boolean | number | string | null;
type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue };

export interface GlobalWebSocketEvent {
  type: string;
  id?: number | string;
  event_id?: number | string;
  message_id?: number | string;
  client_message_id?: string;
  match_id?: number | string;
  job_id?: number | string;
  batch_id?: number | string;
  session_id?: string;
  character_id?: number;
  character_name?: string;
  character?: { name?: string; [key: string]: unknown };
  timestamp?: string;
  created_at?: string;
  content?: string;
  message?: string;
  message_type?: string;
  system_event_type?: string | null;
  reply_bubbles?: unknown;
  media_url?: string;
  voice_duration?: number;
  transcription?: unknown;
  gift?: GiftReceivedEvent['gift'];
  is_from_character?: boolean;
  is_typing?: boolean;
  typing_statuses?: Record<string, boolean>;
  error?: string;
  detail?: string;
  required_credits?: number;
  cooldown_minutes?: number;
  new_balance?: number;
  current_balance?: number;
  plan_name?: string;
  delay_seconds?: number | string;
  count?: number;
  unread_count?: number;
  session_counts?: Record<string, number>;
  notification?: unknown;
  data?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  status?: string;
  progress?: number;
  [key: string]: unknown;
}

export interface GlobalWebSocketOutboundMessage {
  type: string;
  [key: string]: JsonValue | undefined;
}

export type WebSocketHandler = (data: GlobalWebSocketEvent) => void;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export interface ChatTerminalFailure {
  messageId: number;
  reason: string;
}

export function getChatTerminalFailure(event: GlobalWebSocketEvent): ChatTerminalFailure | null {
  if (event.type !== 'chat_status' || event.status !== 'error' || !isRecord(event.metadata)) {
    return null;
  }

  const { outcome, reason } = event.metadata;
  if (
    outcome !== 'failed' ||
    typeof reason !== 'string' ||
    typeof event.message_id !== 'number' ||
    !Number.isSafeInteger(event.message_id) ||
    event.message_id <= 0
  ) {
    return null;
  }

  return { messageId: event.message_id, reason };
}

// A job id correlates every update of one run, so it identifies the job — never
// the single event. Keying dedup on it collapses a whole progress stream into
// one identity and freezes live UIs. Job events carry no per-event id, so they
// stay undeduped here; their consumers merge by their own item ids instead.
export function getStableEventIdentity(event: GlobalWebSocketEvent): string | null {
  const directId = event.event_id ?? event.message_id ?? event.batch_id ?? event.match_id ?? event.id;
  if (typeof directId === 'string' || typeof directId === 'number') {
    return `${event.type}:${event.session_id ?? 'global'}:${directId}`;
  }

  if (isRecord(event.notification)) {
    const notificationId = event.notification.id;
    if (typeof notificationId === 'string' || typeof notificationId === 'number') {
      return `${event.type}:notification:${notificationId}`;
    }
  }

  return null;
}

export interface GlobalWebSocketContextType {
  isConnected: boolean;
  subscribe: (handler: WebSocketHandler) => () => void;
  subscribeToSession: (sessionId: string, handler: WebSocketHandler) => () => void;
  sendMessage: (message: GlobalWebSocketOutboundMessage) => void;
  cancelPendingGameAction: (clientMoveId: string) => boolean;
  joinSession: (sessionId: string) => void;
  leaveSession: (sessionId: string) => void;
  isSessionJoined: (sessionId: string) => boolean;
}

export const GlobalWebSocketContext = createContext<GlobalWebSocketContextType | undefined>(undefined);

export function useGlobalWebSocket(): GlobalWebSocketContextType {
  const context = useContext(GlobalWebSocketContext);
  if (context === undefined) {
    throw new Error('useGlobalWebSocket must be used within a GlobalWebSocketProvider');
  }
  return context;
}
